/**
 * Keyword catalog. Used by mechanics dispatcher and damage resolver.
 *
 * Keywords whose behavior takes parameters store their numbers / payload on
 * `Card.keywordConfig` (see types/card.ts), NOT in the chip text. The chip
 * text is just the keyword name (e.g. "Reflect", "Disarm", "Perry").
 *
 * Keywords:
 *  - Quick (attack-only): defender cannot activate counters or play utility cards in the
 *    reaction window — they may only defend, armor block, or take damage.
 *  - Crush (attack-only): when blocked by armor, +1 wear counter (2 instead of 1).
 *  - Serrated (attack-only): when this attack deals damage, opponent's armor gains +1 wear.
 *  - Stun: on attack-deals-damage OR defend-full-blocks-attack, stunned player's next action is
 *    skipped; the other player becomes aggressor with priority.
 *  - Reflect X (defend-only): on full block, queue a synthetic attack of power X back at
 *    attacker; defender becomes aggressor with priority.
 *  - Perry (defend-only): on full block, payload effects fire (per keywordConfig.perry).
 *  - Displace: attack-deals-damage OR blocked-by-armor → opponent's stance is discarded.
 *    Defend full-blocks → opponent's stance is disarmed (TBD per design — see engine).
 *  - Disarm X: attack-deals-damage OR defend-full-blocks → add X disarm tokens / disarm X
 *    opponent weapons. Tokens are consumed on the next ready (recovery).
 *  - Piercing: attack cannot be blocked by armor.
 *  - Chain: on attack-deals-damage, if opponent has another card with this keyword, trigger it as a follow-up attack.
 *  - Negate: a utility-side keyword. When a card with Negate is played/activated
 *    in response to an opponent's stack entry, it pops that entry: refunds stamina,
 *    reverses any state changes from playing it, and prevents its effect. Used by
 *    utility cards (e.g. Saw it Coming, Pocket Sand) and activated counter effects.
 *    Gating for WHAT a Negate card can respond to lives on the card's
 *    `reactionTriggers` data — Negate itself is the pure mechanic.
 */
import type { Card, KeywordSide, PerryConfig } from "@/types/card";

export type Keyword =
  | "Stun"
  | "Quick"
  | "Piercing"
  | "Reflect"
  | "Disarm"
  | "Crush"
  | "Displace"
  | "Perry"
  | "Chain"
  | "Serrated"
  | "Negate";

export const ALL_KEYWORDS: Keyword[] = [
  "Stun",
  "Quick",
  "Piercing",
  "Reflect",
  "Disarm",
  "Crush",
  "Displace",
  "Perry",
  "Chain",
  "Serrated",
  "Negate",
];

/** Keywords that only make sense on the attack side. UI may filter the
 *  defend-keywords editor against this list. */
export const ATTACK_ONLY_KEYWORDS: ReadonlySet<string> = new Set([
  "Crush",
  "Serrated",
  "Chain",
  "Piercing",
  "Quick",
]);

/** Keywords that only make sense on the defend side. */
export const DEFEND_ONLY_KEYWORDS: ReadonlySet<string> = new Set([
  "Reflect",
  "Perry",
]);

/** Keywords that require a numeric value when granted (e.g. Reflect 2, Disarm 3).
 *  Used by the effect editor to show a value input next to the keyword dropdown,
 *  and by the engine to populate the granted buff's amount field. */
export const KEYWORDS_WITH_AMOUNT: ReadonlySet<string> = new Set([
  "Reflect",
  "Disarm",
]);

/** True when the keyword takes a numeric value (e.g. Reflect 2). */
export function keywordTakesAmount(kw: string | undefined): boolean {
  if (!kw) return false;
  // Tolerate legacy chip text like "Reflect 2" — strip the trailing number.
  const head = kw.split(/\s+/)[0] ?? "";
  return KEYWORDS_WITH_AMOUNT.has(head);
}

/** True when the keyword (or its legacy variant) is in the list. */
export function hasKeyword(list: string[] | undefined, kw: Keyword | string): boolean {
  if (!list) return false;
  // Reflect: tolerate legacy chips like "Reflect 2" — still recognized.
  if (kw === "Reflect" || kw.startsWith("Reflect")) {
    return list.some((k) => k === "Reflect" || k.startsWith("Reflect"));
  }
  return list.includes(kw);
}

/** Read a slot-specific keyword instance config. Falls back to the legacy
 *  shared `keywordConfig` when no slot config is set. Returns undefined if
 *  neither is set for this keyword. */
export function slotConfig(
  card: Card | undefined,
  side: KeywordSide,
  keyword: "reflect" | "disarm" | "perry",
): number | PerryConfig | undefined {
  if (!card) return undefined;
  const slot = card.keywordSlots?.[side]?.[keyword];
  if (slot) {
    const v = (slot as any)[keyword];
    if (v !== undefined) return v;
  }
  // Legacy shared fallback.
  const legacy = card.keywordConfig?.[keyword];
  return legacy as any;
}

/** Reflect amount on a card's defend slot. Reads slot config first, then
 *  legacy shared config, then legacy "Reflect N" chip text. */
export function reflectAmount(card: Card | undefined): number {
  if (!card) return 0;
  // Defend-slot first.
  const slot = card.keywordSlots?.defend?.reflect?.reflect;
  if (typeof slot === "number") return Math.max(0, slot);
  // Legacy shared.
  const cfg = card.keywordConfig?.reflect;
  if (typeof cfg === "number") return Math.max(0, cfg);
  // Legacy chip text fallback.
  const lists: Array<string[] | undefined> = [
    card.keywords,
    card.attackKeywords,
    card.defendKeywords,
  ];
  for (const list of lists) {
    if (!list) continue;
    for (const k of list) {
      const m = k.match(/^Reflect\s+(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
  }
  if (lists.some((l) => l && l.some((k) => k === "Reflect"))) return 1;
  return 0;
}

/** Disarm count for a card on its attack slot. Slot config first, then
 *  legacy shared, default 1 when keyword is present but no number set. */
export function disarmCount(card: Card | undefined): number {
  if (!card) return 0;
  const slot = card.keywordSlots?.attack?.disarm?.disarm;
  if (typeof slot === "number") return Math.max(0, slot);
  const cfg = card.keywordConfig?.disarm;
  if (typeof cfg === "number") return Math.max(0, cfg);
  const lists: Array<string[] | undefined> = [
    card.keywords,
    card.attackKeywords,
    card.defendKeywords,
  ];
  if (lists.some((l) => l && l.includes("Disarm"))) return 1;
  return 0;
}

/** Perry payload for a card on its defend slot. Slot config first, then
 *  legacy shared. Returns undefined when no Perry config is set. */
export function perryPayload(card: Card | undefined): PerryConfig | undefined {
  if (!card) return undefined;
  const slot = card.keywordSlots?.defend?.perry?.perry;
  if (slot) return slot;
  return card.keywordConfig?.perry;
}
